import * as _ from 'lodash';
import { booleanAttribute, ChangeDetectionStrategy, Component, computed, CUSTOM_ELEMENTS_SCHEMA, effect, ElementRef, EventEmitter, inject, Input, input, Output, TemplateRef, viewChild } from '@angular/core';

import { pageMode, TPageMode } from 'src/app/infrastructure/models/page-mode';
import { sectionLayout } from 'src/app/infrastructure/models/section-layout';
import { PagerMode } from 'src/app/features/shared/pager/model/pager-show-mode';
import { centralPageAnimation } from 'src/app/infrastructure/animations/central-page-animations';
import { slideAnimation } from 'src/app/infrastructure/animations/slide-animation';
import { showAnimation } from 'src/app/infrastructure/animations/show-animation';
import { FooterService } from '../../../footer/footer.service';
import { SharedModule } from '../../../shared.module';
import { CompositePageComponent } from '../composite-page/composite.component';
import { SectionDetailsTabLayoutComponent } from '../section-details-tab-layout/section-details-tab-layout.component';
import { TablistHeaderComponent } from '../tablist/container/tablist-header/tablist-header.component';
import { TabFooterComponent } from '../tablist/container/tab-footer/tab-footer.component';
import { ButtonsGroupComponent } from '../../../footer/components/buttons-group/buttons-group.component';
import { LoadingPageService } from 'src/app/infrastructure/services/loading-services/loading-page.service';
import { AnimationService } from 'src/app/infrastructure/services/loading-services/animation.service';
import { ActivatedRouteService } from 'src/app/infrastructure/services/route-service/activated-route.service';
import { ApplicationUserService } from 'src/app/infrastructure/services/application-user.services/application-user.service';
import { _FormModelService } from 'src/app/infrastructure/services/model-services/_form-model.service';
import { TabListService } from '../../services/tab-list.service';
import { appConfig } from 'src/config';
import { TabPageStorageService } from 'src/app/features/shared/storage/services/storage-services/tab-page-storage.service';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';
import { motionDuration_read } from 'src/app/infrastructure/animations/motion-duration';

@Component({
    selector: 'app-tab-page',
    templateUrl: './tab-page.component.html',
    styleUrls: [
        '../../central-page.scss',
        './tab-page.component.scss',
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    host: {
        '[class]': '`tab-page central-page ${pageLayout$$()}-layout ${pageMode$$()}-mode${scrollReady$$() ? " scroll-ready" : ""}`',
    },
    standalone: true,
    imports: [
        ButtonsGroupComponent,
        CompositePageComponent,
        TabFooterComponent,
        SectionDetailsTabLayoutComponent,
        SharedModule,
        TablistHeaderComponent,
    ],
    providers: [
        TabPageStorageService,
    ],
    animations: [
        centralPageAnimation,
        showAnimation,
        slideAnimation,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TabPageComponent
{
    private readonly __el = inject(ElementRef);

    protected readonly _activatedRouteService = inject(ActivatedRouteService, {optional: true});
    protected readonly _animationService = injectRequired(AnimationService);
    protected readonly _footerService = injectRequired(FooterService);
    protected readonly _loadingPageService = injectRequired(LoadingPageService);
    // protected readonly _tabDataService = injectRequired(TabDataService);
    protected readonly _tabListService = injectRequired(TabListService);
    protected readonly _applicationUserService = injectRequired(ApplicationUserService);

    // Paginawissel-duur = gedeelde animatieduur van het thema (--layout-motion-duration, zie motion-duration.ts),
    // gelezen bij elke standwissel zodat een thema-wijziging direct meetelt.
    protected _motionDuration$$ = computed(() => { this.pageMode$$(); return motionDuration_read(); });
    protected _showHideEnterTime$$ = this._motionDuration$$;
    protected _showHideLeaveTime$$ = this._motionDuration$$;

    pageSize: number = appConfig.pager_size;
    pagerMode: PagerMode = 'hidden';

    animating$$ = this._tabListService.animating$$;
    dragging$$ = this._tabListService.dragging$$;
    // Gated scroll: alleen in een stabiele details-/medium-/edit-stand (niet tijdens
    // slepen of een lopende animatie) mag de details-sectie scrollbaar worden.
    scrollReady$$ = computed(() => {
        if (this.dragging$$() || this.animating$$()) { return false; }
        const _mode = this.pageMode$$();
        return _mode === pageMode.mediumPage
            || _mode === pageMode.detailsPage
            || _mode === pageMode.editPage
            || _mode === pageMode.createPage;
    });
    sectionHeight$$ = this._tabListService.sectionHeight$$;
    btnCancelData$$ = this._tabListService.btnCancelData$$;
    btnDeleteData$$ = this._tabListService.btnDeleteData$$;
    btnEditData$$ = this._tabListService.btnEditData$$;
    btnSaveData$$ = this._tabListService.btnSaveData$$;

    sectionDetails_buttons_template$$ = this._tabListService.sectionDetails_buttons_template$$;
    // Details-knoppen in de footerbalk alleen bij verborgen tab (details-page); zolang de tab zichtbaar is
    // (tab-page, medium-page) is de footerbalk voor de tab en staat Edit in de tab-footer. Zie slide-animation.ts.
    sectionDetails_show$$ = computed(() => this.pageMode$$() == pageMode.detailsPage);
    sectionEdit_buttons_template$$ = this._tabListService.sectionEdit_buttons_template$$;
    sectionEdit_show$$ = computed(() => this.pageMode$$() == pageMode.editPage);

    loadingPage$$ = computed(() => this._loadingPageService.isLoading$$());
    pageLayout$$ = this._tabListService.pageLayout$$;
    pageMode$$ = this._tabListService.pageMode$$;
    pageName$$ = this._tabListService.pageName_sectionDetails$$;
    tabIndex$$ = this._tabListService.tabIndex$$;
    tabIndex$ = this._tabListService.tabIndex$;
    // tabName$$ = this._tabDataService.tabName$$;
    tabVisible$$ = this._tabListService.tabVisible$$;
    tabList$$ = this._tabListService.tabList$$;
    tabList_buttons_template$$ = computed(() => this.tabList$$()?.map((item, index) => {
        return {
            id: index,
            template: item.templateButtons
        };
    }));
    tabList_footer_templates$$ = computed(() => this.tabList$$()?.map(item => item.templateFooter));
    tabIsPanel$$ = this._tabListService.tabIsPanel$$;
    sectionLayout$$ = this._tabListService.sectionLayout$$;
    isContentLayout$$ = computed(() => this.sectionLayout$$() === sectionLayout.content);
    tabList_templates$$ = computed(() => this.tabList$$()?.map((item, index) => {
        return {
            id: index,
            template: item.templateList,
        };
    }));

    footerContentBlock$$ = viewChild<TemplateRef<any>>('footerContentBlock');

    draggable$$ = input(false, {alias: 'draggable', transform: booleanAttribute});
    locked$$ = input(false, {alias: 'locked', transform: booleanAttribute});
    resizeHandle_show$$ = computed(() => this.draggable$$() && !this.locked$$() && this.tabVisible$$());

    // Minimale sleepafstand (px) vóór een sleepbeweging ook echt als standwissel wordt
    // gecommit (height_dragCommit) — zonder deze drempel wisselt een enkele klik (pointerdown
    // zonder verplaatsing) al meteen van stand (met animatie). De hoogte zelf volgt de
    // vinger/muis wél al vanaf de allereerste beweging (zie emit_resizeStart/-Move), zodat de
    // sleeprand niet eerst een dode zone doorloopt om dan in één keer bij te springen.
    private static readonly __dragThreshold = 8;

    private __dragCommitted = false;
    private __dragStartHeight = 0;
    private __dragStartY: number | null = null;

    @Input() pageMode: TPageMode = pageMode.tabPage;

    @Output('btnDelete') btnDeleteEmit = new EventEmitter();
    @Output('btnEdit') btnEditEmit = new EventEmitter();
    @Output('btnCancel') btnCancelEmit = new EventEmitter();
    @Output('btnSave') btnSaveEmit = new EventEmitter();
    @Output() pageModeEmit = new EventEmitter<TPageMode>();

    constructor(){
        effect(() => this._footerService.contentBlock_template_set(this.footerContentBlock$$()));
        effect(() => this._tabListService.draggable_set(this.draggable$$()));
        effect(() => this._tabListService.locked_set(this.locked$$()));
    }


    emit_btnCancel(): void {
        this.btnCancelEmit.emit();
    }

    emit_btnDelete(): void {
        this.btnDeleteEmit.emit();
    }

    emit_btnEdit(): void {
        this.btnEditEmit.emit();
    }

    emit_btnSave(): void {
        this.btnSaveEmit.emit();
    }

    emit_resizeStart(e: PointerEvent): void {
        e.preventDefault();
        this.__dragStartY = e.clientY;
        this.__dragCommitted = false;
        (e.target as HTMLElement).setPointerCapture(e.pointerId);

        const _el = this.__detailsSection_el();
        this.__dragStartHeight = _el?.clientHeight ?? 0;

        if (_el) {
            const _container = _el.parentElement?.clientHeight ?? 0;
            const _fontPx = parseFloat(getComputedStyle(_el).fontSize) || 16;
            this._tabListService.presetGeometry_set(_container, 8 * _fontPx);
        }

        // Live hoogte-tracking start meteen (geen dode zone); de standwissel zelf wordt pas
        // gecommit in emit_resizeMove zodra __dragThreshold is overschreden.
        this._tabListService.height_dragStart(this.__dragStartHeight);
    }

    emit_resizeMove(e: PointerEvent): void {
        if (this.__dragStartY == null) {return;}

        const _delta = e.clientY - this.__dragStartY;

        if (!this.__dragCommitted && Math.abs(_delta) >= TabPageComponent.__dragThreshold) {
            this.__dragCommitted = true;
            this._tabListService.height_dragCommit();
        }

        const _el = this.__detailsSection_el();
        const _container = _el?.parentElement?.clientHeight ?? 0;
        const _min = 64;
        const _max = Math.max(_min, _container - 64);
        const _height = Math.min(_max, Math.max(_min, this.__dragStartHeight + _delta));

        this._tabListService.height_dragMove(_height);
    }

    emit_resizeEnd(e: PointerEvent): void {
        if (this.__dragStartY == null) {return;}

        this.__dragStartY = null;
        (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);

        if (this.__dragCommitted) {
            this.__dragCommitted = false;
            this._tabListService.height_dragEnd();
        } else {
            this._tabListService.height_dragCancel();
        }
    }

    emit_slideBottom_start(): void {
        this.animation_start();
    }

    emit_slideBottom_done(): void {
        this.animation_done();
    }

    emit_slideTop_start(): void {
        this.animation_start();
    }
    
    emit_slideTop_done(): void {
        this.animation_done();
    }
    
    emit_touchEnd(e: TouchEvent, when: string) {
        this._tabListService.tab_swipe(e, when);
    }

    emit_touchStart(e: TouchEvent, when: string) {
        this._tabListService.tab_swipe(e, when);
    }

    animation_start(): void {
        this._animationService.add();
    }

    animation_done(): void {
        this._animationService.remove();
    }

    private __detailsSection_el(): HTMLElement | null {
        return (this.__el.nativeElement as HTMLElement).querySelector('.details-section');
    }

    trackById(item: any): number {
        return item.id;
    }
}
